import * as React from "react";
import getConfig from "next/config";
import { DataLayer } from "@grandvisionhq/analytics";
import { CmsHeadTags, CmsGraphProductBreadcrumbs, useBloomreachPdpContent, useBloomreachPageRoutes, useBloomreachSiteLogos, useBloomreachDeliveryInfo, CmsComponentSlot } from "@grandvisionhq/bloomreach";
import { MiniCartModal, Upsell } from "@grandvisionhq/carts-v2";
import { useLink } from "@grandvisionhq/frk-routing";
import { ProductAnalyticsEvents, useProductData, useProductDetailSlots, ProductDetailLayoutSlotsProvider, WebPageSchema, ProductDetailFullWidth, UnpublishedProductEcommContent, UnpublishedProductImage, ProductDetailTitle, ProductDetailLayout, useAddToProductViewHistory, SINGLE_PURCHASE, ProductDetailSlotsProvider, ProductProvider, CmsProvider, TealiumPageView, ProductStructuredData, LensesPerBoxText, CLPackText, VariantTreeStockIndicator, FrameSizeText, SizeGuideModal, ProductDetailUspsSection, StarRatingReview } from "@grandvisionhq/fun-products";
import { WishlistButton } from "@grandvisionhq/fun-wishlist/button";
import { ErrorPage } from "@grandvisionhq/layout";
import { ProductBlockSlotsProvider, LabelledAlert } from "@grandvisionhq/lib-visuals";
import { ReviewsProvider } from "@grandvisionhq/reviews";
import { DeliveryIndicatorProvider } from "@grandvisionhq/shipping";
import { useFeatureToggles, useIntl, createFeatureConfigs, createServerFeatureConfigs } from "@grandvisionhq/state";
import { monetate } from "@grandvisionhq/svc-monetate";
import { extractCurrentRoute, isNonEmptyObject, EnvironmentUtils, isArray } from "@grandvisionhq/utils";
import { P as ProductRecommendationContainer } from "./ProductRecommendationContainer.js";
import "@grandvisionhq/svc-tealium/events";
import { C as CrossSellProductsGraphContainer } from "./CrossSellProductsGraphContainer.js";
import { D as DynamicDeliveryIndicatorContainer } from "./DynamicDeliveryIndicatorContainer.js";
import "@grandvisionhq/reviews/dist/index.css";
import "./algolia.js";
import "next/router";
import { Types } from "@grandvisionhq/graphql";
const useAddPDPEventsToDataLayer = (props) => {
  React.useEffect(() => {
    ProductAnalyticsEvents.pushPdpProductDetailEvent(props);
    DataLayer.pushPageTypeToDataLayer("product detail");
  }, []);
};
const addOverrideToPath = (path, slug, override) => {
  const slugIndex = path.indexOf(slug);
  if (slugIndex === -1) {
    return path;
  }
  const beforeSlug = path.slice(0, slugIndex);
  const afterSlug = path.slice(slugIndex);
  return `${beforeSlug}${override}/${afterSlug}`;
};
const extractProductFromRoute = (route, excludeSku) => {
  const currentRoute = extractCurrentRoute(route);
  const splitRoute = currentRoute.split("/");
  let slug;
  let sku;
  if (excludeSku) {
    slug = splitRoute[splitRoute.length - 1];
  }
  if (!excludeSku && splitRoute.length > 1) {
    slug = splitRoute[splitRoute.length - 2];
    sku = splitRoute[splitRoute.length - 1];
  }
  return {
    slug,
    sku
  };
};
const ProductPageRoutingInformation = ({
  children
}) => {
  const { activeVariant, productUrl, product } = useProductData();
  return /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement(
    CmsHeadTags,
    {
      title: activeVariant.seoConfig.metaTitle,
      description: activeVariant.seoConfig.metaDescription,
      ogImage: activeVariant.seoConfig.ogImageUrl,
      ogUrl: productUrl
    },
    children
  ), /* @__PURE__ */ React.createElement(
    CmsGraphProductBreadcrumbs,
    {
      variant: activeVariant,
      productType: product.productType,
      brandName: product.brandName
    }
  ));
};
const UnpublishedProductPage = () => {
  const { Slots: OpcoSlots } = useProductDetailSlots();
  return /* @__PURE__ */ React.createElement(
    ProductDetailLayoutSlotsProvider,
    {
      slots: {
        ProductTitle: () => /* @__PURE__ */ React.createElement(ProductDetailTitle, null),
        ImageGallery: () => /* @__PURE__ */ React.createElement(UnpublishedProductImage, null),
        PrimaryProductInfo: () => null,
        Usps: () => null,
        EcommContent: () => /* @__PURE__ */ React.createElement(UnpublishedProductEcommContent, null),
        SecondaryProductInfo: () => /* @__PURE__ */ React.createElement(ProductDetailFullWidth, null, OpcoSlots.SecondaryProductInfoSlot && /* @__PURE__ */ React.createElement(OpcoSlots.SecondaryProductInfoSlot, null)),
        StickyCta: () => null,
        Analytics: () => /* @__PURE__ */ React.createElement(WebPageSchema, null),
        StarRatingReview: () => null
      }
    },
    /* @__PURE__ */ React.createElement(ProductDetailLayout, null)
  );
};
const UnpublishedProductPageGuard = ({
  children
}) => {
  const { product, activeVariant } = useProductData();
  return !product.isPublished || !activeVariant.isPublished ? /* @__PURE__ */ React.createElement(UnpublishedProductPage, null) : /* @__PURE__ */ React.createElement(React.Fragment, null, children);
};
const { publicRuntimeConfig } = getConfig();
const selfUrl = publicRuntimeConfig?.selfUrl || "";
const { apiKey: powerReviewsApiKey, groupId, opcoId } = publicRuntimeConfig?.powerReviews || {};
const ProductPageBase = (props) => {
  const content = useBloomreachPdpContent();
  const { routes } = useBloomreachPageRoutes();
  const siteLogos = useBloomreachSiteLogos();
  const {
    isReviewsEnabled,
    isMiniCartRecommendationsEnabled,
    isDynamicLineItemDeliveryEstimationEnabled,
    isPdpMissingContentErrorEnabled
  } = useFeatureToggles();
  const deliveryInfo = useBloomreachDeliveryInfo();
  const { getLabel } = useIntl();
  const link = useLink({ route: "home" });
  useAddToProductViewHistory({ variant: props.activeVariant, variants: props.product.variants });
  const {
    sku,
    product,
    activeVariant,
    facetConfigList,
    specificationConfiguration,
    deliveryIndicatorConfig,
    bookAppointmentRouteConfig
  } = props;
  const { isReviewEnabled: isProductReviewEnabled } = product;
  const { recommendedCategoryKey } = activeVariant;
  const reviewsProviderProps = {
    enabled: isReviewsEnabled && !!isProductReviewEnabled,
    config: {
      groupId,
      opcoId,
      apiKey: powerReviewsApiKey,
      writeReviewPath: routes["write-review"] ?? ""
    }
  };
  useAddPDPEventsToDataLayer({
    displayPrice: activeVariant.purchaseTypePrices[SINGLE_PURCHASE],
    purchaseType: DataLayer.DataLayerTypes.PurchaseType.OneTime,
    masterSku: product.masterSku,
    product,
    activeVariant
  });
  React.useEffect(() => {
    monetate().trackProductDetail([
      {
        sku: activeVariant.sku,
        productId: product.id
      }
    ]);
  }, [activeVariant]);
  const slots = React.useMemo(
    () => ({
      SecondaryProductInfoSlot: CrossSellProductsGraphContainer,
      WishlistButton,
      ...props.slots,
      ...isDynamicLineItemDeliveryEstimationEnabled ? { DynamicDeliveryIndicator: DynamicDeliveryIndicatorContainer } : {}
    }),
    [isDynamicLineItemDeliveryEstimationEnabled, props.slots]
  );
  const { TopBanner } = slots;
  if (isPdpMissingContentErrorEnabled && !isNonEmptyObject(content)) {
    return /* @__PURE__ */ React.createElement(
      ErrorPage,
      {
        header: getLabel("common.pageNotFound.header"),
        subheader: getLabel("common.pageNotFound.subheader"),
        buttonText: getLabel("cart.actions.goBack"),
        buttonHref: link?.href
      }
    );
  }
  return /* @__PURE__ */ React.createElement(ProductDetailSlotsProvider, { slots }, /* @__PURE__ */ React.createElement(DeliveryIndicatorProvider, { config: deliveryIndicatorConfig, deliveryInfo }, /* @__PURE__ */ React.createElement(
    ProductProvider,
    {
      selfUrl,
      product,
      sku,
      specificationConfiguration: specificationConfiguration ?? [],
      routes,
      facetConfigList,
      bookAppointmentRouteConfig
    },
    /* @__PURE__ */ React.createElement(
      ProductBlockSlotsProvider,
      {
        slots: {
          WishlistButton
        }
      },
      /* @__PURE__ */ React.createElement(ReviewsProvider, { ...reviewsProviderProps }, /* @__PURE__ */ React.createElement(
        CmsProvider,
        {
          disclaimer: content.disclaimer,
          brandPromotions: content.brandPromotions ?? [],
          sizeGuideDocument: content.sizeGuideDocument,
          usps: content.usps,
          purchaseTypeDocument: content.purchaseTypeDocument,
          contactLensesInfoModal: content.modalContent,
          reimbursableLogos: {
            default: siteLogos.reimbursable,
            alternative: siteLogos.reimbursableAlternative
          }
        },
        /* @__PURE__ */ React.createElement(TealiumPageView, null, TopBanner ? /* @__PURE__ */ React.createElement(TopBanner, null) : null, /* @__PURE__ */ React.createElement(CmsComponentSlot, { path: "main/top-container" }), /* @__PURE__ */ React.createElement(ProductPageRoutingInformation, null, isReviewsEnabled && /* @__PURE__ */ React.createElement("link", { rel: "preconnect", href: "https://ui.powerreviews.com" })), /* @__PURE__ */ React.createElement(UnpublishedProductPageGuard, null, props.children), /* @__PURE__ */ React.createElement(CmsComponentSlot, { path: "main/bottom-container" }))
      )),
      /* @__PURE__ */ React.createElement(ProductStructuredData, null)
    )
  )), /* @__PURE__ */ React.createElement(MiniCartModal, { links: { cart: routes.cart, checkout: routes.checkout } }, !isMiniCartRecommendationsEnabled && recommendedCategoryKey ? (addedLineItems) => /* @__PURE__ */ React.createElement(Upsell, { recommendedCategoryKey, lineItems: addedLineItems }) : (addedLineItems) => /* @__PURE__ */ React.createElement(
    ProductRecommendationContainer,
    {
      recommendationContext: addedLineItems.some(({ productType }) => productType === "prescription-glasses") ? "MINICART_RX" : "MINICART",
      skus: [sku]
    }
  )));
};
const errorProps404 = { statusCode: 404 };
const errorProps500 = { statusCode: 500 };
const handleValidations = (validationError, errorMsg) => {
  console.error(errorMsg, JSON.stringify(validationError, null, 2));
  switch (validationError.failure) {
    // Server side validation errors
    case "cannot-extract-slug-and-locale":
    case "no-usable-sku":
      return errorProps404;
    case "no-client":
    case "general-error": {
      return errorProps500;
    }
    // Queried Product validation errors
    case "no-product-found":
    case "incorrect-typename": {
      return errorProps404;
    }
    case "no-variants": {
      return errorProps500;
    }
  }
};
const getPriceSegmentKey = (ctx) => {
  const { priceSegmentQueryKey = "", priceSegmentCookieAgeInHours = 48 } = createFeatureConfigs(
    EnvironmentUtils.getFeatureConfigs()
  );
  const { priceSegmentAllowedReferrers: allowedReferrers } = createServerFeatureConfigs(
    EnvironmentUtils.getServerFeatureConfigs()
  );
  const PRICE_COOKIE_MAX_AGE_IN_HOURS = priceSegmentCookieAgeInHours;
  const maxAge = Math.round(PRICE_COOKIE_MAX_AGE_IN_HOURS * 60 * 60);
  let priceSegmentChannelKey = ctx.query?.[priceSegmentQueryKey];
  if (isArray(priceSegmentChannelKey)) {
    priceSegmentChannelKey = void 0;
  }
  const isRefererAllowed = !allowedReferrers || allowedReferrers.length === 0 || allowedReferrers.some((allowedReferrer) => ctx.req?.headers?.referer?.includes(allowedReferrer));
  if (priceSegmentChannelKey && isRefererAllowed) {
    ctx.res?.setHeader(
      "Set-Cookie",
      `${priceSegmentQueryKey}=${priceSegmentChannelKey}; Secure; SameSite=Lax; Path=/; Max-Age=${maxAge}`
    );
    return priceSegmentChannelKey;
  }
  return ctx.req?.cookies[priceSegmentQueryKey];
};
const setupInitialConfig = (ctx) => {
  const { asPath = "", apolloClient, req, pageConfig } = ctx;
  const locale = req?.locale?.toString();
  const { slug, sku: routeSku } = extractProductFromRoute(
    asPath,
    pageConfig?.excludeSkuFromProductUrls
  );
  if (!slug || !locale) {
    return {
      failure: "cannot-extract-slug-and-locale",
      message: `Can't extract slug and locale from ${asPath}, got slug: '${String(
        slug
      )}' and locale: '${String(locale)}'`
    };
  }
  if (!apolloClient) {
    return {
      failure: "no-client",
      message: `No Apollo client provided in PDP page context for slug: "${slug}"`
    };
  }
  return {
    slug,
    routeSku,
    locale,
    client: apolloClient
  };
};
const fetchProductConfigBySlug = async (ctx, queryFunction, productMapperFn, facetConfigList, flatFacetConfigList) => {
  const initialConfig = setupInitialConfig(ctx);
  if ("failure" in initialConfig) {
    return initialConfig;
  }
  const { slug, locale, client, routeSku } = initialConfig;
  let product;
  try {
    const queryResult = await queryFunction({ client, slug, locale });
    if (queryResult?.error) {
      throw queryResult.error;
    }
    const serverProduct = queryResult?.data.getProductBySlug.product;
    if (serverProduct === null || serverProduct === void 0) {
      return { failure: "no-product-found", message: `No product found for slug: "${slug}"` };
    }
    const priceSegmentChannelKey = getPriceSegmentKey(ctx);
    const maybeProduct = productMapperFn(
      serverProduct,
      slug,
      locale,
      facetConfigList,
      flatFacetConfigList,
      priceSegmentChannelKey
    );
    if ("failure" in maybeProduct) {
      return maybeProduct;
    }
    product = maybeProduct;
  } catch (err) {
    console.error(err);
    return {
      failure: "general-error",
      message: `Failed to fetch product with slug: "${String(slug)}"`
    };
  }
  const activeVariant = (
    /**
     * Typescript v4 is unable to handle union types with the .find() method.
     * More information here: https://github.com/microsoft/TypeScript/issues/44373
     *
     * The issue is fixed in v5, so we can remove the @ts-expect-error and the
     * cast for `const activeVariant: Variant` when we upgrade.
     */
    // @ts-expect-error ts(2349)
    product.variants.find((variant) => variant.sku === routeSku) || product.variants[0]
  );
  const sku = routeSku ?? activeVariant?.sku;
  if (!sku)
    return {
      failure: "no-usable-sku",
      message: `No usable SKU found for product with id: "${product.id}"`
    };
  let bloomreachPathOverride;
  const overrideKey = product?.commercialCategories?.[0]?.key;
  if (overrideKey) {
    bloomreachPathOverride = addOverrideToPath(ctx.asPath || "", slug, overrideKey);
  }
  return {
    product,
    activeVariant,
    sku,
    slug,
    locale,
    bloomreachPathOverride
  };
};
const defaultContactLensFacetConfig = [
  {
    facetKey: "color",
    displayType: "thumbnail",
    showSingleFacetAs: "text-with-heading",
    titleLabelKey: "products.facets.color.title",
    titleLabelKeySingle: "products.facets.color.title.single",
    onFacetSelection: ProductAnalyticsEvents.pushPdpVariantClickEvent
  },
  {
    facetKey: "lensesPerBox",
    displayType: "text-node",
    showSingleFacetAs: "text-with-heading",
    titleLabelKey: "products.contactLenses.boxQuantity",
    facetTermFormatter: ({ facetTerm }) => /* @__PURE__ */ React.createElement(CLPackText, { lensesPerBox: facetTerm }),
    singleFacetFormatter: ({ facetTerm }) => /* @__PURE__ */ React.createElement(LensesPerBoxText, { lensesPerBox: facetTerm }),
    onFacetSelection: ProductAnalyticsEvents.pushPdpVariantClickEvent
  }
];
const defaultSolutionsFacetConfig = [
  {
    facetKey: "packageOption",
    displayType: "text-node",
    showSingleFacetAs: "none",
    titleLabelKey: "products.facets.volume.title",
    onFacetSelection: ProductAnalyticsEvents.pushPdpVariantClickEvent
  }
];
const defaultProductWithAFrameFacetConfig = [
  {
    facetKey: "color",
    displayType: "thumbnail",
    showSingleFacetAs: "text-without-heading",
    slots: {
      Right: VariantTreeStockIndicator
    },
    titleLabelKey: "products.facets.frameColor.title",
    titleLabelKeySingle: "products.facets.color.title.single",
    onFacetSelection: ({ color, ...props }) => {
      ProductAnalyticsEvents.pushPdpVariantClickEvent({ ...props, dimension147: color });
    }
  },
  {
    facetKey: "size",
    displayType: "text-node",
    showSingleFacetAs: "display-type",
    slots: {
      Right: VariantTreeStockIndicator,
      Bottom: SizeGuideModal
    },
    requiresUserSelection: true,
    titleLabelKey: "products.facets.frameSize.title",
    facetTermFormatter: FrameSizeText,
    notification: ({ notificationType }) => /* @__PURE__ */ React.createElement(LabelledAlert, { variant: notificationType, labelKey: "products.facets.frameSize.notification" }),
    onFacetSelection: ({ size, ...props }) => {
      ProductAnalyticsEvents.pushPdpVariantClickEvent({ ...props, frameSize: size });
    },
    sortCompareFunction: (a, b) => {
      const frameSizeMap = {
        [Types.FrameSize.EXTRA_SMALL]: 1,
        [Types.FrameSize.SMALL]: 2,
        [Types.FrameSize.MEDIUM]: 3,
        [Types.FrameSize.LARGE]: 4
      };
      const aOrder = frameSizeMap[a.facetTerm];
      const bOrder = frameSizeMap[b.facetTerm];
      return aOrder - bOrder;
    }
  }
];
const defaultAccessoriesFacetConfig = [
  {
    facetKey: "color",
    displayType: "thumbnail",
    showSingleFacetAs: "text-without-heading",
    slots: {
      Right: VariantTreeStockIndicator
    },
    titleLabelKey: "products.numberOfVariants",
    titleLabelKeySingle: "products.facets.color.title.single",
    onFacetSelection: ({ color, ...props }) => {
      ProductAnalyticsEvents.pushPdpVariantClickEvent({ ...props, dimension147: color });
    }
  },
  {
    facetKey: "other",
    displayType: "text-node",
    showSingleFacetAs: "text-without-heading",
    slots: {
      Right: VariantTreeStockIndicator
    },
    titleLabelKey: "products.numberOfVariants",
    onFacetSelection: ({ color, ...props }) => {
      ProductAnalyticsEvents.pushPdpVariantClickEvent({ ...props, dimension147: color });
    }
  }
];
const defaultReadyReaderFacetConfig = [
  {
    facetKey: "power",
    displayType: "dropdown",
    showSingleFacetAs: "display-type",
    slots: {
      Right: VariantTreeStockIndicator
    },
    titleLabelKey: "products.readyreaders.power",
    onFacetSelection: ({ power }) => {
      DataLayer.pushEvent({
        event: "readyreaders",
        eventAction: "pdp select lens power",
        eventLabel: power
      });
    }
  }
];
const ProductPageLayoutBase = ({
  EcommContent,
  PrimaryProductInfo,
  StickyCta,
  SecondaryProductInfo,
  ImageGallery
}) => {
  const slots = React.useMemo(
    () => ({
      Analytics: WebPageSchema,
      EcommContent,
      ImageGallery,
      PrimaryProductInfo,
      ProductTitle: ProductDetailTitle,
      SecondaryProductInfo,
      StarRatingReview,
      StickyCta,
      Usps: ProductDetailUspsSection
    }),
    [EcommContent, ImageGallery, PrimaryProductInfo, SecondaryProductInfo, StickyCta]
  );
  return /* @__PURE__ */ React.createElement(ProductDetailLayoutSlotsProvider, { slots }, /* @__PURE__ */ React.createElement(ProductDetailLayout, null));
};
export {
  ProductPageLayoutBase as P,
  ProductPageBase as a,
  defaultContactLensFacetConfig as b,
  defaultProductWithAFrameFacetConfig as c,
  defaultAccessoriesFacetConfig as d,
  defaultReadyReaderFacetConfig as e,
  fetchProductConfigBySlug as f,
  defaultSolutionsFacetConfig as g,
  handleValidations as h
};
//# sourceMappingURL=ProductPageLayoutBase.js.map
